/* PartnerInvitesPanel — trackable per-recipient invitations.

   Approved partners (RP + MP) can create unique invitation links for
   specific businesses or families. Each invite carries a token that
   locks in the inviter as `referred_by_partner_id` at signup time —
   guaranteeing commission attribution even if the recipient typos a
   referral code.

   - Regional Partners can invite both Funeral Homes (MP) and Families
   - Memorial Partners can invite Families only
*/
import React, { useCallback, useEffect, useState } from 'react';
import { Send, Copy, X, Check, Mail, Clock, Building2, Users } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { suggestCorrectEmail } from '../lib/emailValidation';
import { toast } from 'sonner';

const STATUS_PILL = {
  pending:   'bg-amber-50 text-amber-700 border border-amber-200',
  accepted:  'bg-emerald-50 text-emerald-700 border border-emerald-200',
  cancelled: 'bg-slate-100 text-slate-600 border border-slate-200',
  expired:   'bg-slate-100 text-slate-500 border border-slate-200',
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso.slice(0, 10); }
}

export function PartnerInvitesPanel({ isRegional }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    // Default to a FAMILY invite (the "begin your memorial" link). RPs
    // must explicitly pick Funeral Home to send an MP-signup link —
    // the old MP default caused families to receive partner-form links.
    invite_kind: 'family',
    target_email: '',
    target_name: '',
    target_business_name: '',
    target_phone: '',
    note: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/partners/me/invites');
      setRows(data.rows || []);
    } catch (e) {
      // Silent fail on first load (collection may not exist yet).
      setRows([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.target_email.trim()) {
      toast.error('Email is required');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post('/partners/me/invites', {
        ...form,
        target_email: form.target_email.trim().toLowerCase(),
        target_name: form.target_name.trim() || null,
        target_business_name: form.target_business_name.trim() || null,
        target_phone: form.target_phone.trim() || null,
        note: form.note.trim() || null,
      });
      // Optimistic prepend so the new row appears instantly.
      setRows((r) => [data, ...r]);
      setForm((f) => ({ ...f,
        target_email: '', target_name: '', target_business_name: '',
        target_phone: '', note: '',
      }));
      // Auto-copy the URL to clipboard as a fallback (in case email is delayed).
      try { await navigator.clipboard.writeText(data.url); } catch { /* ignore */ }
      if (data.email_last_delivered) {
        toast.success(`Invitation emailed to ${data.target_email}`);
      } else {
        toast.warning('Invite created — email delivery is offline. Link copied to clipboard so you can send it manually.');
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSubmitting(false); }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch { toast.error('Copy failed — select and copy manually'); }
  };

  const resend = async (row) => {
    if (!window.confirm(`Resend the invitation email to ${row.target_email}?`)) return;
    try {
      const { data } = await api.post(`/partners/me/invites/${row.invite_id}/resend`);
      setRows((r) => r.map((x) => x.invite_id === row.invite_id
        ? { ...x, email_last_delivered: !!data.email_delivered,
            email_last_sent_at: new Date().toISOString(),
            email_send_count: (x.email_send_count || 0) + 1 }
        : x));
      if (data.email_delivered) {
        toast.success(`Email resent to ${row.target_email}`);
      } else {
        toast.warning('Email delivery is offline right now. The signing link is still valid — use Copy link to share it manually.');
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cancel = async (id) => {
    if (!window.confirm('Cancel this invitation? The link will stop working.')) return;
    try {
      await api.post(`/partners/me/invites/${id}/cancel`);
      setRows((r) => r.map((x) => x.invite_id === id
        ? { ...x, status: 'cancelled', cancelled_at: new Date().toISOString() }
        : x));
      toast.success('Cancelled');
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section
      className="mb-12 rounded-3xl bg-white border border-[hsl(var(--primary)/0.12)] shadow-[0_10px_30px_-20px_rgba(26,54,38,0.18)] p-8"
      data-testid="partner-invites-panel"
    >
      <div className="flex items-start gap-3 flex-wrap justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--accent))] font-semibold flex items-center gap-2">
            <Send size={14} /> Invitations
          </div>
          <h2 className="font-serif text-2xl mt-2 text-[hsl(var(--primary))]">
            Invite a {isRegional ? 'funeral home or family' : 'family'} directly
          </h2>
          <p className="text-sm text-[hsl(var(--primary)/0.7)] mt-2 max-w-xl leading-relaxed">
            Create a one-tap signup link for a specific recipient. The link locks you in
            as their agent of record — every package they buy pays you commission
            automatically. Copy and send via email, text, or DM.
          </p>
        </div>
      </div>

      {/* Create form */}
      <div className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="invite-form">
        {isRegional && (
          <div className="sm:col-span-2">
            <label className="ml-overline">Who are you inviting?</label>
            <div className="mt-2 flex gap-2">
              {[
                { id: 'family',           label: 'Family — begin a memorial', Icon: Users },
                { id: 'memorial_partner', label: 'Funeral Home / Business',   Icon: Building2 },
              ].map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, invite_kind: id }))}
                  className={`flex-1 flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                    form.invite_kind === id
                      ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent)/0.08)] text-[hsl(var(--primary))]'
                      : 'border-[hsl(var(--primary)/0.15)] text-[hsl(var(--primary)/0.7)] hover:border-[hsl(var(--primary)/0.3)]'
                  }`}
                  data-testid={`invite-kind-${id}`}
                >
                  <Icon size={16} /> {label}
                </button>
              ))}
            </div>
          </div>
        )}
        <div>
          <label className="ml-overline" htmlFor="inv-email">Recipient email *</label>
          <input
            id="inv-email"
            type="email"
            value={form.target_email}
            onChange={(e) => setForm((f) => ({ ...f, target_email: e.target.value }))}
            placeholder="they@example.com"
            className="ml-input mt-2"
            data-testid="invite-email"
          />
          {(() => {
            const sug = suggestCorrectEmail(form.target_email);
            if (!sug) return null;
            return (
              <div className="mt-1.5 text-xs text-amber-700 flex items-center gap-1.5" data-testid="invite-email-typo-hint">
                <span>Did you mean</span>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, target_email: sug }))}
                  className="font-semibold underline underline-offset-2 hover:text-amber-900"
                  data-testid="invite-email-typo-fix"
                >{sug}</button>
                <span>?</span>
              </div>
            );
          })()}
        </div>
        <div>
          <label className="ml-overline" htmlFor="inv-name">Contact name</label>
          <input
            id="inv-name"
            type="text"
            value={form.target_name}
            onChange={(e) => setForm((f) => ({ ...f, target_name: e.target.value }))}
            placeholder="Jane Smith"
            className="ml-input mt-2"
            data-testid="invite-name"
          />
        </div>
        {form.invite_kind === 'memorial_partner' && (
          <div className="sm:col-span-2">
            <label className="ml-overline" htmlFor="inv-biz">Business name</label>
            <input
              id="inv-biz"
              type="text"
              value={form.target_business_name}
              onChange={(e) => setForm((f) => ({ ...f, target_business_name: e.target.value }))}
              placeholder="Smith Funeral Home"
              className="ml-input mt-2"
              data-testid="invite-business"
            />
          </div>
        )}
        <div>
          <label className="ml-overline" htmlFor="inv-phone">Phone (optional)</label>
          <input
            id="inv-phone"
            type="tel"
            value={form.target_phone}
            onChange={(e) => setForm((f) => ({ ...f, target_phone: e.target.value }))}
            placeholder="555-123-4567"
            className="ml-input mt-2"
            data-testid="invite-phone"
          />
        </div>
        <div>
          <label className="ml-overline" htmlFor="inv-note">Private note</label>
          <input
            id="inv-note"
            type="text"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            placeholder="Only you see this"
            className="ml-input mt-2"
            data-testid="invite-note"
          />
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={submit}
          disabled={submitting || !form.target_email.trim()}
          className="ml-btn-gold"
          data-testid="invite-submit"
        >
          <Send size={14} />
          {submitting ? 'Sending…' : 'Send invitation'}
        </button>
      </div>

      {/* Pending list */}
      <div className="mt-10">
        <h3 className="font-serif text-xl text-[hsl(var(--primary))]">Your invitations</h3>
        {loading ? (
          <div className="mt-3 text-sm text-[hsl(var(--primary)/0.6)]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="mt-3 text-sm text-[hsl(var(--primary)/0.6)]">
            No invitations sent yet. Use the form above to create your first one.
          </div>
        ) : (
          <ul className="mt-4 divide-y divide-[hsl(var(--primary)/0.06)]" data-testid="invite-list">
            {rows.map((r) => (
              <li key={r.invite_id} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`invite-row-${r.invite_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full ${STATUS_PILL[r.status] || 'bg-slate-50 text-slate-600 border border-slate-200'}`}>
                      {r.status}
                    </span>
                    <span className="text-xs uppercase tracking-widest text-[hsl(var(--primary)/0.55)] font-medium">
                      {r.invite_kind === 'memorial_partner' ? 'Funeral Home' : 'Family'}
                    </span>
                  </div>
                  <div className="mt-1 font-medium text-[hsl(var(--primary))] truncate">
                    {r.target_business_name || r.target_name || r.target_email}
                  </div>
                  <div className="text-[11px] text-[hsl(var(--primary)/0.6)] flex items-center gap-3 flex-wrap mt-0.5">
                    <span className="flex items-center gap-1"><Mail size={11}/> {r.target_email}</span>
                    <span className="flex items-center gap-1"><Clock size={11}/> sent {fmtDate(r.created_at)}</span>
                    {typeof r.email_last_delivered === 'boolean' && (
                      <span
                        className={`flex items-center gap-1 font-semibold ${r.email_last_delivered ? 'text-emerald-700' : 'text-amber-700'}`}
                        title={r.email_last_delivered ? 'Email delivered via SendGrid' : 'Email delivery offline — share the link manually'}
                      >
                        {r.email_last_delivered
                          ? <><Check size={11}/> Email sent{r.email_send_count > 1 ? ` (${r.email_send_count}×)` : ''}</>
                          : <>⚠ Email queued — provider offline</>}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {r.status === 'pending' && (
                    <>
                      <button onClick={() => resend(r)} className="ml-btn-outline !py-1.5 !px-3 text-xs !border-[hsl(var(--accent))] !text-[hsl(var(--primary))]" data-testid={`invite-resend-${r.invite_id}`}>
                        <Mail size={12} /> Resend email
                      </button>
                      <button onClick={() => copy(r.url)} className="ml-btn-outline !py-1.5 !px-3 text-xs" data-testid={`invite-copy-${r.invite_id}`}>
                        <Copy size={12} /> Copy link
                      </button>
                      <button onClick={() => cancel(r.invite_id)} className="ml-btn-outline !py-1.5 !px-3 text-xs !border-red-300 !text-red-700" data-testid={`invite-cancel-${r.invite_id}`}>
                        <X size={12} /> Cancel
                      </button>
                    </>
                  )}
                  {r.status === 'accepted' && (
                    <span className="text-xs text-emerald-700 flex items-center gap-1 font-semibold">
                      <Check size={12} /> Joined {fmtDate(r.accepted_at)}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
